import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations'
import i18nConfig from 'utils/i18nConfig'
import Layout from 'components/layout/Layout'
import EncyclopediaCategory from 'features/encyclopedia/category/EncyclopediaCategory'
import { shuffleArray } from 'utils/helpers'
import { DEFAULT_NAMESPACES } from 'constants/i18n'
import type { SeoProps } from 'components/seo/types'
import { cmsLib } from 'lib'
import { mapCmsEncyclopediaArticlesToArticles } from 'utils/cms'
import { EncyclopediaCategoryProps } from 'features/encyclopedia/category/types'

const EncyclopediaCategoryPage = (props: EncyclopediaCategoryProps) => {
  // Constants
  const { seo, title } = props.category
  const SEO: SeoProps = {
    description: seo?.metaDescription,
    image: seo?.seoImage?.url,
    title: seo?.seoTitle ?? title
  }

  return (
    <Layout seo={SEO}>
      <EncyclopediaCategory {...props} />
    </Layout>
  )
}

export const getStaticProps = async ({ params, locale }) => {
  const { category: categorySlug } = params

  // Fetch the data
  const [category, subCategories, encyclopediaArticles] = await Promise.all([
    cmsLib.getEncyclopediaCategoryBySlug(categorySlug),
    cmsLib.getEncyclopediaSubcategoriesByCategorySlug(categorySlug),
    cmsLib.getEncyclopediaArticles()
  ])

  if (!category) {
    return {
      notFound: true,
      revalidate: 5
    }
  }

  // Convert the articles (for filtering & using in the components)
  const articles = mapCmsEncyclopediaArticlesToArticles(encyclopediaArticles)

  // Get articles for category and filter out the highlighted article & popular articles
  const categoryArticles = articles.filter((article) => article.category.slug === categorySlug)
  const highlightedArticle = categoryArticles.find((article) => article.isHighlighted || article.isHighlightedLanding) || null
  const popularArticles = shuffleArray(categoryArticles.filter((article) => article.isPopular)).slice(0, 4)

  return {
    props: {
      category,
      highlightedArticle,
      popularArticles,
      subCategories,
      ...(await serverSideTranslations(locale, [...DEFAULT_NAMESPACES, 'encyclopedia'], i18nConfig))
    },
    revalidate: 10 // re-generate page when a request comes in, once every 10 seconds
  }
}

export const getStaticPaths = async () => {
  const paths = []

  const categories = await cmsLib.getEncyclopediaCategories()

  categories.forEach((cat) => {
    const categoryPaths = [
      { locale: 'nl', params: { category: cat.slug } }
      // { params: { category: cat.slug }, locale: 'fr' }
    ]
    paths.push(...categoryPaths)
  })

  return {
    fallback: 'blocking',
    paths // will server-render pages on-demand if the path does not exist
  }
}

export default EncyclopediaCategoryPage
